/**
 * ONE HOME: every URL an older Home answered, and where it lands now.
 *
 * Nath, 4 October 2026: "All other homes need to go, we need to make this the
 * default." Louis's Home (`features/home-workspace-preview/`) serves `/home`
 * for everyone. Three Homes held paths before it, and their URLs are in
 * bookmarks, chat, push payloads and signage playlists this repo cannot
 * reach, so each lands somewhere deliberate rather than on the `**` wildcard:
 *
 *   /home-v3 · /home-preview · /home-legacy            → /home
 *   /home{,-legacy}/my-day · tasks                     → /home
 *   /home-legacy/my-week                              → /home/my-week (Louis's My week, inside Home)
 *   /home-legacy/bookings · services                   → /home/bookings · /home/services
 *   /home-legacy/article/:id                           → /article/:id
 *   /home-preview/communities · community/:id          → /communities · /communities/:id
 *   /home-preview/write · write/:id                    → /write · /write/:id
 *   /preview/bookings · /preview/services              → /preview/home-workspace-live/bookings · /services
 *
 * The four sub-pages were the gen-1 Home's. Today, This week and My bookings
 * are Louis's Home's own now, so its front door is the right place for all
 * four. The gen-2 Home's CHILDREN are not Homes: the communities and the
 * writer moved to paths of their own (Nath, 4 October 2026), and their old
 * URLs carry the id and the query across. `/home-preview/write/compose` and
 * `/drafts` need no line of their own: `write/:id` carries "compose" and
 * "drafts" to the matching routes, which are declared before `write/:id`.
 *
 * The gen-1 Home's My bookings and Services pages had no-auth review
 * harnesses, `/preview/bookings` and `/preview/services`. The pages and the
 * harnesses are deleted (5 October 2026); their URLs land on the same page in
 * Home's live-shape harness, and the old `?empty=1` asks that harness for its
 * empty scenario.
 *
 * ⚠ A SHARED POST OR ARTICLE KEEPS ITS VIEWER. Home V3 and the gen-2 Home
 * both mounted `im-hub-viewer-host`, so `?post=` and `?read=` on their paths
 * are shared links to a post or an article. Louis's Home mounts the same host
 * (Today's featured stories open in it, on live data while the tenant has
 * Hubs), so carrying the query to `/home` opens what was shared over the one
 * Home, and closing it leaves the reader there.
 *
 * ⚠ EVERY REDIRECT IS THE FUNCTION FORM, FOR TWO REASONS. A string
 * `redirectTo` that starts with `/` rebuilds the URL from the redirect string
 * alone, so the query (`?highlight=`, `?post=`, campaign tags) is dropped. A
 * relative string keeps the query but forbids any further redirect, so a
 * target that is not routed yet fails the navigation outright. A function
 * that returns a `UrlTree` keeps what it is given and re-matches from the
 * root, so a target that is itself a redirect still lands.
 *
 * Framework-light on purpose, like `settings-retired-routes.ts`: it imports
 * no component, so `app.routes.ts` can spread it without growing the initial
 * bundle.
 */
import { inject } from '@angular/core';
import { Router, type RedirectFunction, type Routes } from '@angular/router';

import { HOME_HARNESS_ROUTE, HOME_LIVE_HARNESS_ROUTE, HOME_ROUTE } from '../utils/home-link';

/** The Services page, inside Home (home-views.ts). `/services` itself redirects here too. */
export const SERVICES_ROUTE = '/home/services';

/** My bookings, inside Home (home-views.ts). */
export const BOOKINGS_ROUTE = '/home/bookings';

/** My week, inside Home (home-views.ts). */
export const MY_WEEK_ROUTE = '/home/my-week';

/**
 * The gen-1 Home's sub-pages that Louis's Home took over. Bookings and Services are not among them:
 * they are pages inside Home now, which claims `/home/bookings` and `/home/services` before these lines.
 */
export const RETIRED_HOME_SUB_PAGES = ['my-day', 'tasks'] as const;

/** A redirect to `path`, carrying the whole query and the fragment. */
function redirectKeepingQuery(path: string): RedirectFunction {
  return ({ queryParams, fragment }) =>
    inject(Router).createUrlTree([path], {
      queryParams,
      fragment: fragment ?? undefined,
    });
}

/** An older Home's URL: Louis's Home, with any shared post or article still open. */
export const toOneHome: RedirectFunction = redirectKeepingQuery(HOME_ROUTE);

/** The old Services sub-page: the Services page. */
export const toServicesPage: RedirectFunction = redirectKeepingQuery(SERVICES_ROUTE);

/** The old My bookings sub-page: My bookings. */
export const toBookingsPage: RedirectFunction = redirectKeepingQuery(BOOKINGS_ROUTE);

/** The gen-1 Home's My week: Louis's My week, with its query (`?period=`). */
export const toMyWeekPage: RedirectFunction = redirectKeepingQuery(MY_WEEK_ROUTE);

/**
 * A deleted page's review harness: the same page in Home's live-shape harness. The old harnesses
 * read `?empty=1` for their zero state, which the live harness calls `?scenario=empty`.
 */
function redirectToHarnessPage(page: 'bookings' | 'services'): RedirectFunction {
  return ({ queryParams, fragment }) => {
    const { empty, ...rest } = queryParams;
    const query = empty === '1' || empty === 'true' ? { scenario: 'empty', ...rest } : rest;
    return inject(Router).createUrlTree([HOME_LIVE_HARNESS_ROUTE, page], {
      queryParams: query,
      fragment: fragment ?? undefined,
    });
  };
}

/** `/preview/bookings` and `/preview/services`, the deleted pages' harnesses. */
export const toBookingsHarness: RedirectFunction = redirectToHarnessPage('bookings');
export const toServicesHarness: RedirectFunction = redirectToHarnessPage('services');

/** A path under `base`, carrying the `:id` the old URL named, the query and the fragment. */
function redirectWithId(base: string): RedirectFunction {
  return ({ params, queryParams, fragment }) =>
    inject(Router).createUrlTree([base, params['id']], {
      queryParams,
      fragment: fragment ?? undefined,
    });
}

/** An article share (`/home/article/:id`, AC-H-8, and its gen-1 twin): `/article/:id`, its query (a campaign tag) kept. */
export const toArticle: RedirectFunction = redirectWithId('/article');

/** The retired Homes' own harnesses (`/preview/home-v3`, `/preview/home-spotlight`): Louis's Home on its fixtures. */
export const toHomeHarness: RedirectFunction = redirectKeepingQuery(HOME_HARNESS_ROUTE);

/** The gen-2 Home's community directory and communities: `/communities`. */
export const toCommunities: RedirectFunction = redirectKeepingQuery('/communities');
export const toCommunity: RedirectFunction = redirectWithId('/communities');

/** The gen-2 Home's writer: `/write`. */
export const toWriter: RedirectFunction = redirectKeepingQuery('/write');
export const toWriterPage: RedirectFunction = redirectWithId('/write');

/**
 * The redirects, in the order `app.routes.ts` declares them. Each matches its
 * path in full, so none of them can claim a child of a path it retires.
 */
export const RETIRED_HOME_ROUTES: Routes = [
  // Home V3's build-out path. V3 served `/home` itself until 4 October 2026.
  { path: 'home-v3', pathMatch: 'full', redirectTo: toOneHome },
  // The gen-2 Home. Its communities and writer routes are declared separately and stay.
  { path: 'home-preview', pathMatch: 'full', redirectTo: toOneHome },
  // The gen-1 Home, and the sub-pages it owned under both of its paths.
  { path: 'home-legacy', pathMatch: 'full', redirectTo: toOneHome },
  ...RETIRED_HOME_SUB_PAGES.flatMap((page) => [
    { path: `home/${page}`, pathMatch: 'full' as const, redirectTo: toOneHome },
    { path: `home-legacy/${page}`, pathMatch: 'full' as const, redirectTo: toOneHome },
  ]),
  { path: 'home-legacy/bookings', pathMatch: 'full', redirectTo: toBookingsPage },
  // My week is a page inside Home again (5 October 2026): the gen-1 path lands on it.
  { path: 'home-legacy/my-week', pathMatch: 'full', redirectTo: toMyWeekPage },
  { path: 'home-legacy/services', pathMatch: 'full', redirectTo: toServicesPage },
  // The twin of `/home/article/:id` (AC-H-8): the gen-1 Home's article overlay wrote this one into history.
  { path: 'home-legacy/article/:id', pathMatch: 'full', redirectTo: toArticle },
  // The gen-2 Home's children, at their own paths now: links in chat, push payloads and signage playlists.
  { path: 'home-preview/communities', pathMatch: 'full', redirectTo: toCommunities },
  { path: 'home-preview/community/:id', pathMatch: 'full', redirectTo: toCommunity },
  { path: 'home-preview/write', pathMatch: 'full', redirectTo: toWriter },
  { path: 'home-preview/write/:id', pathMatch: 'full', redirectTo: toWriterPage },
  // The deleted pages' review harnesses. `data.preview` keeps the preloader off them, like every `/preview/*` route.
  { path: 'preview/bookings', pathMatch: 'full', redirectTo: toBookingsHarness, data: { preview: true } },
  { path: 'preview/services', pathMatch: 'full', redirectTo: toServicesHarness, data: { preview: true } },
  { path: 'preview/home-v3', pathMatch: 'full', redirectTo: toHomeHarness, data: { preview: true } },
  { path: 'preview/home-spotlight', pathMatch: 'full', redirectTo: toHomeHarness, data: { preview: true } },
];
